/**
 * Generic undoable-delete helper: shows a toast with a Deshacer action and
 * only commits the deletion after a 5s grace period. Framework-free — the
 * caller wires onCommit/onRestore to its own state and services.
 */

import { showToast } from './toast.js';

export interface UndoableDeleteOptions {
  /** Runs when the grace period expires (the actual delete). */
  onCommit: () => Promise<void> | void;
  /** Runs on undo and when the commit fails (rollback optimistic UI). */
  onRestore: () => void;
  message?: string;
  actionLabel?: string;
}

const GRACE_MS = 5000;

/**
 * Schedules a delete that can be undone for 5 seconds.
 * Returns a cancel function that prevents the commit without restoring
 * (e.g. when the owner component unmounts and the delete should be dropped).
 */
export function scheduleUndoableDelete({
  onCommit,
  onRestore,
  message = 'Eliminado',
  actionLabel = 'Deshacer',
}: UndoableDeleteOptions): () => void {
  let settled = false;

  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    void (async () => {
      try {
        await onCommit();
      } catch {
        onRestore();
        showToast('No se pudo eliminar');
      }
    })();
  }, GRACE_MS);

  const undo = () => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    onRestore();
  };

  showToast(message, { actionLabel, duration: GRACE_MS, onAction: undo });

  return () => {
    settled = true;
    clearTimeout(timer);
  };
}
